import { useNavigate, useSearch } from "@tanstack/react-router";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCan } from "@/lib/auth";

import { useRequests, useSummary } from "../api";
import { AccountingView } from "../components/AccountingView";
import { AdminView } from "../components/AdminView";
import { DetailView } from "../components/DetailView";
import { HowToView } from "../components/HowToView";
import { SummaryView } from "../components/SummaryView";

export type SwagTab = "summary" | "detail" | "admin" | "accounting" | "how-to";

export function MajorSwagPage() {
  const isAdmin = useCan("major_swag.admin");
  const canRequest = useCan("major_swag.write") || isAdmin;
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { tab?: SwagTab };
  const tab: SwagTab = search.tab ?? "summary";

  const summary = useSummary();
  // Non-admins see their own pending count; admins see the whole queue, which
  // is the number that needs acting on.
  const requests = useRequests("pending");
  const pending = isAdmin
    ? (summary.data?.pending_requests ?? 0)
    : (requests.data?.length ?? 0);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Major Swag</h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          What good swag we have left, where every piece went, and which
          department it gets expensed to. Replaces the “Major Swag Items Usage”
          spreadsheet — including its record of who has already been given
          what, so nobody gets the same gift twice.
        </p>
      </div>

      <Tabs
        value={tab}
        onValueChange={(t) =>
          navigate({
            to: "/major-swag",
            search: { tab: t as SwagTab },
            replace: true,
          })
        }
      >
        <TabsList variant="underline">
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="detail" count={pending > 0 ? pending : undefined}>
            Detail
          </TabsTrigger>
          {isAdmin && <TabsTrigger value="admin">Admin</TabsTrigger>}
          {isAdmin && <TabsTrigger value="accounting">Accounting</TabsTrigger>}
          <TabsTrigger value="how-to">How to use</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-4">
          <SummaryView />
        </TabsContent>
        <TabsContent value="detail" className="mt-4">
          <DetailView canRequest={canRequest} isAdmin={isAdmin} />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="admin" className="mt-4">
            <AdminView />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="accounting" className="mt-4">
            <AccountingView />
          </TabsContent>
        )}
        <TabsContent value="how-to" className="mt-4">
          <HowToView isAdmin={isAdmin} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
